import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Archive, Lock } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import type { Department, Vehicle } from "../../lib/types";
import { useAuth } from "../../lib/auth";
import { Modal } from "../core/Modal";
import { Button } from "../core/Button";
import { Field, Input, Select, Textarea } from "../core/Field";
import { TenantSelect, useMeta } from "./TenantSelect";
import { CategoryPicker } from "./CategoryPicker";

interface Props {
  open: boolean;
  onClose: () => void;
  existing?: Vehicle | null;
  onSaved?: (v: Vehicle) => void;
  lockedTenantId?: string;
  onArchive?: () => void;
}

const empty = { tenant_id: "", plate_number: "", category: "passenger", sub_category: "Car", make: "", model: "", year: "", color: "", chassis_no: "", engine_no: "", fuel_type: "Petrol", department_id: "", status: "active", odometer_km: "", notes: "", geotab_device_id: "" };

function Section({ title, hint, locked, children, testId }: { title: string; hint: string; locked?: boolean; children: React.ReactNode; testId: string }) {
  return (
    <section data-testid={testId} className={`rounded-lg border p-4 ${locked ? "border-slate-200 bg-slate-50/70" : "border-slate-200"}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <div><p className="text-xs font-bold uppercase tracking-wider text-slate-700">{title}</p><p className="text-[11px] text-slate-500">{hint}</p></div>
        {locked && <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-600"><Lock className="h-3 w-3" />Back-office only</span>}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{children}</div>
    </section>
  );
}

export function VehicleForm({ open, onClose, existing, onSaved, lockedTenantId, onArchive }: Props) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: meta } = useMeta();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const isBO = user?.user_type === "super_admin";
  const canIdentity = isBO;
  const tenantId = existing?.tenant_id || (isBO ? form.tenant_id : user?.tenant_id || "");

  const { data: departments } = useQuery<Department[]>({
    queryKey: ["departments", tenantId],
    queryFn: async () => (await api.get("/departments", { params: { tenant_id: isBO ? tenantId : undefined } })).data,
    enabled: open && !!tenantId,
  });

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setForm({
        ...empty, tenant_id: existing.tenant_id, plate_number: existing.plate_number, category: existing.category, sub_category: existing.sub_category || "", make: existing.make || "", model: existing.model || "",
        year: existing.year?.toString() || "", color: existing.color || "", chassis_no: existing.chassis_no || "", engine_no: existing.engine_no || "", fuel_type: existing.fuel_type || "", department_id: existing.department_id || "",
        status: existing.status, odometer_km: existing.odometer_km?.toString() || "", notes: existing.notes || "", geotab_device_id: existing.geotab_device_id || "",
      });
    } else setForm({ ...empty, tenant_id: lockedTenantId || "" });
  }, [open, existing, lockedTenantId]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setCategory = (v: string) => setForm((f) => ({ ...f, category: v, sub_category: meta?.categories[v]?.sub[0] || "" }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload: Record<string, unknown> = {
        year: form.year ? Number(form.year) : null, color: form.color || null, chassis_no: form.chassis_no || null, engine_no: form.engine_no || null,
        fuel_type: form.fuel_type || null, department_id: form.department_id || "", status: form.status,
        odometer_km: form.odometer_km ? Number(form.odometer_km) : null, notes: form.notes || null,
      };
      if (canIdentity) Object.assign(payload, { category: form.category, sub_category: form.sub_category || null, make: form.make || null, model: form.model || null });
      let saved: Vehicle;
      if (existing) {
        if (isBO && form.plate_number !== existing.plate_number) payload.plate_number = form.plate_number;
        saved = (await api.put(`/vehicles/${existing.id}`, payload)).data;
      } else {
        saved = (await api.post("/vehicles", { ...payload, tenant_id: form.tenant_id, plate_number: form.plate_number, department_id: form.department_id || null })).data;
      }
      toast.success(existing ? "Vehicle updated" : `Vehicle ${saved.plate_number} registered`);
      qc.invalidateQueries({ queryKey: ["vehicles"] });
      qc.invalidateQueries({ queryKey: ["vehicle", saved.id] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      onSaved?.(saved);
      onClose();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const subs = meta?.categories[form.category]?.sub || [];
  const idCls = canIdentity ? "" : "bg-slate-100 text-slate-500";
  return (
    <Modal open={open} onOpenChange={(o) => !o && onClose()} title={existing ? `Edit ${existing.plate_number}` : "Register new vehicle"} className="max-w-3xl" testId="vehicle-form-modal"
      description={existing ? (canIdentity ? "Vehicle identity (plate, category, make & model) is maintained by the back-office; everything else can be edited by the client." : "Plate, category, make & model are locked — contact your fleet operator to change them. All other details are yours to update.") : "The back-office registers vehicles and sets their identity. The client maintains the operational details afterwards."}
      footer={<>
        {existing && onArchive && <Button variant="danger" type="button" onClick={onArchive} className="mr-auto" data-testid="archive-vehicle-button"><Archive className="h-3.5 w-3.5" />Archive vehicle</Button>}
        <Button variant="secondary" type="button" onClick={onClose} data-testid="vehicle-cancel-button">Cancel</Button>
        <Button type="submit" form="vehicle-form" loading={busy} data-testid="vehicle-save-button">{existing ? "Save changes" : "Register vehicle"}</Button>
      </>}>
      <form id="vehicle-form" onSubmit={submit} className="space-y-4">
        <Section title="Vehicle identity" hint="Plate number, category, make and model" locked testId="vehicle-section-identity">
          {isBO && !existing && !lockedTenantId && (
            <Field label="Client" className="col-span-2 sm:col-span-4">
              <TenantSelect value={form.tenant_id} onChange={(v) => setForm((f) => ({ ...f, tenant_id: v, department_id: "" }))} allowAll={false} testId="vehicle-tenant-select" />
            </Field>
          )}
          <div className="col-span-2 sm:col-span-4">
            <span className="label">Vehicle category <span className="text-rose-500">*</span></span>
            <div className={canIdentity ? "" : "pointer-events-none opacity-60"}><CategoryPicker value={form.category} onChange={setCategory} testIdPrefix="vehicle-category" /></div>
          </div>
          <Field label="Plate number" hint={canIdentity ? "Uppercase, no spaces stored" : undefined}>
            <Input data-testid="vehicle-plate-input" value={form.plate_number} onChange={set("plate_number")} required disabled={!canIdentity} className={`font-mono uppercase ${idCls}`} placeholder="WXY1234" />
          </Field>
          <Field label="Sub-category">
            <Select data-testid="vehicle-subcategory-select" value={form.sub_category} onChange={set("sub_category")} disabled={!canIdentity} className={idCls}>
              {subs.map((s) => <option key={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Make"><Input data-testid="vehicle-make-input" value={form.make} onChange={set("make")} placeholder="Toyota" disabled={!canIdentity} className={idCls} /></Field>
          <Field label="Model"><Input data-testid="vehicle-model-input" value={form.model} onChange={set("model")} placeholder="Hilux" disabled={!canIdentity} className={idCls} /></Field>
        </Section>

        <Section title="Specifications" hint="Build details and registration numbers" testId="vehicle-section-specs">
          <Field label="Year"><Input data-testid="vehicle-year-input" type="number" value={form.year} onChange={set("year")} min={1980} max={2100} /></Field>
          <Field label="Colour"><Input data-testid="vehicle-color-input" value={form.color} onChange={set("color")} /></Field>
          <Field label="Fuel"><Select data-testid="vehicle-fuel-select" value={form.fuel_type} onChange={set("fuel_type")}><option>Petrol</option><option>Diesel</option><option>Hybrid</option><option>EV</option><option>NGV</option></Select></Field>
          <Field label="Odometer (km)"><Input data-testid="vehicle-odometer-input" type="number" value={form.odometer_km} onChange={set("odometer_km")} /></Field>
          <Field label="Chassis No." className="col-span-2"><Input data-testid="vehicle-chassis-input" value={form.chassis_no} onChange={set("chassis_no")} className="font-mono" /></Field>
          <Field label="Engine No." className="col-span-2"><Input data-testid="vehicle-engine-input" value={form.engine_no} onChange={set("engine_no")} className="font-mono" /></Field>
        </Section>

        <Section title="Operations" hint="Where the vehicle sits in your organisation and how it is tracked" testId="vehicle-section-ops">
          <Field label="Status"><Select data-testid="vehicle-status-select" value={form.status} onChange={set("status")}>{(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}</Select></Field>
          <Field label="Department / Branch" className="col-span-2 sm:col-span-2">
            <Select data-testid="vehicle-department-select" value={form.department_id} onChange={set("department_id")} disabled={!tenantId}>
              <option value="">Unassigned</option>
              {(departments || []).map((d) => <option key={d.id} value={d.id}>{d.name} ({d.unit_type})</option>)}
            </Select>
          </Field>
          <Field label="Notes" className="col-span-2 sm:col-span-4"><Textarea data-testid="vehicle-notes-input" value={form.notes} onChange={set("notes")} rows={2} /></Field>
        </Section>
      </form>
    </Modal>
  );
}
